import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Trash2,
  Plus,
  MapPin,
  Calendar,
  Search,
  X,
  ImagePlus,
  Upload,
  Loader2,
  Check,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { parse } from "exifr";
import { travelPhotos } from "@/data/photos";
import type { TravelPhoto } from "@/data/photos";

const ITEMS_PER_PAGE = 50;

/**
 * 将文件转换为压缩后的 base64 缩略图
 * 提高画质：使用 400px 尺寸和 0.85 质量
 */
function fileToCompressedBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_SIZE = 400;
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d")!;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * 从 EXIF 日期字符串解析 Date 对象
 * 支持格式：
 *   - "2023:05:20 14:30:00" (标准 EXIF)
 *   - "2026/4/22 13:39" (Windows 属性格式)
 *   - "2023-05-20T14:30:00" (ISO 格式)
 */
function parseExifDate(exifDate: any): Date | null {
  if (!exifDate) return null;
  try {
    const str = exifDate.toString().trim();
    
    // 处理 "2023:05:20 14:30:00" 或 "2026/4/22 13:39" 格式
    if (str.includes(" ")) {
      const [datePart] = str.split(" ");
      // 尝试用 ":" 或 "/" 分割日期
      const separators = [":", "/", "-"];
      for (const sep of separators) {
        if (datePart.includes(sep)) {
          const parts = datePart.split(sep).map(Number);
          if (parts.length >= 3 && parts[0] > 1900 && parts[0] < 2100) {
            return new Date(parts[0], parts[1] - 1, parts[2]);
          }
        }
      }
    }
    
    // 尝试直接解析
    const d = new Date(str);
    if (!isNaN(d.getTime())) return d;
  } catch { /* ignore */ }
  return null;
}

function parseGpsCoord(value: any, ref?: string): number | null {
  if (value == null) return null;
  if (typeof value === "number") {
    let coord = value;
    if (ref === "S" || ref === "W") coord = -coord;
    return coord;
  }
  if (typeof value === "string") {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      let coord = num;
      if (ref === "S" || ref === "W") coord = -coord;
      return coord;
    }
  }
  if (Array.isArray(value)) {
    const [deg, min, sec] = value;
    if (deg == null || min == null) return null;
    let decimal =
      Math.abs(Number(deg)) +
      Number(min) / 60 +
      (sec != null ? Number(sec) / 3600 : 0);
    if (ref === "S" || ref === "W") decimal = -decimal;
    return decimal;
  }
  return null;
}

interface ParsedItem {
  preview: string;
  status: "parsing" | "success" | "error";
  photo?: TravelPhoto;
  fileName: string;
}

export default function PhotosPage() {
  const [customPhotos, setCustomPhotos] = useState<TravelPhoto[]>(() => {
    try {
      const saved = localStorage.getItem("customPhotos");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPhotos, setSelectedPhotos] = useState<Set<number>>(new Set());
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [parsingItems, setParsingItems] = useState<ParsedItem[]>([]);
  const [page, setPage] = useState(1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState<TravelPhoto | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  // 性能优化：用 ref 存储正在处理的数据，避免在循环中频繁 setState 导致重渲染
  const parsingItemsRef = useRef<ParsedItem[]>([]);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const allPhotos = useMemo(() => {
    const combined = [...travelPhotos, ...customPhotos];
    if (!searchQuery.trim()) return combined;
    const q = searchQuery.toLowerCase();
    return combined.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        p.city.toLowerCase().includes(q) ||
        p.date.includes(q)
    );
  }, [customPhotos, searchQuery]);

  const totalPages = Math.ceil(allPhotos.length / ITEMS_PER_PAGE);
  const displayedPhotos = allPhotos.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const toggleSelection = (id: number) => {
    setSelectedPhotos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedPhotos.size === displayedPhotos.length) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(new Set(displayedPhotos.map((p) => p.id)));
    }
  };

  const deleteSelected = () => {
    const newCustom = customPhotos.filter((p) => !selectedPhotos.has(p.id));
    setCustomPhotos(newCustom);
    localStorage.setItem("customPhotos", JSON.stringify(newCustom));
    setSelectedPhotos(new Set());
    setPage(1); // 重置页码
    setDeleteConfirmOpen(false);
    toast.success(`已删除 ${selectedPhotos.size} 张照片`);
  };

  const processFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (fileArray.length === 0) return;

    // 性能优化：只存储预览 URL 和文件名，不保留 File 对象
    const initialItems: ParsedItem[] = fileArray.map((file) => ({
      preview: URL.createObjectURL(file),
      status: "parsing" as const,
      fileName: file.name,
    }));
    
    // 同时更新 state 和 ref
    parsingItemsRef.current = initialItems;
    setParsingItems(initialItems);

    // 性能优化：批量处理，每 5 张为一组，避免内存溢出
    const BATCH_SIZE = 5;
    const parsedPhotos: TravelPhoto[] = [];

    for (let batchStart = 0; batchStart < fileArray.length; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, fileArray.length);
      const batch = fileArray.slice(batchStart, batchEnd);

      for (let j = 0; j < batch.length; j++) {
        const file = batch[j];
        const idx = batchStart + j;

        try {
          const exif = await parse(file, { gps: true });
          const lat =
            parseGpsCoord(exif?.latitude, exif?.GPSLatitudeRef) ??
            parseGpsCoord(exif?.GPSLatitude, exif?.GPSLatitudeRef);
          const lng =
            parseGpsCoord(exif?.longitude, exif?.GPSLongitudeRef) ??
            parseGpsCoord(exif?.GPSLongitude, exif?.GPSLongitudeRef);

          if (lat != null && lng != null) {
            // 使用压缩后的 base64 缩略图
            const thumbnail = await fileToCompressedBase64(file);
            
            // 从 EXIF 中提取拍摄日期
            let photoDate = new Date().toISOString().split("T")[0];
            let photoYear = new Date().getFullYear();
            
            // 尝试多种 EXIF 日期字段
            const parsedDate = parseExifDate(exif?.DateTimeOriginal) 
              || parseExifDate(exif?.CreateDate) 
              || parseExifDate(exif?.ModifyDate)
              || parseExifDate(exif?.DateTime);
            
            if (parsedDate) {
              photoDate = parsedDate.toISOString().split("T")[0];
              photoYear = parsedDate.getFullYear();
            }
            
            const newPhoto: TravelPhoto = {
              id: Date.now() + idx + Math.random(),
              lat,
              lng,
              date: photoDate,
              year: photoYear,
              thumbnail,
              title: file.name.replace(/\.[^/.]+$/, ""),
              mood: "",
              city: "未知地点",
              country: "中国",
            };

            parsedPhotos.push(newPhoto);

            // 性能优化：只更新 ref，不触发重渲染
            parsingItemsRef.current[idx] = { ...parsingItemsRef.current[idx], photo: newPhoto, status: "success" };
          } else {
            parsingItemsRef.current[idx] = { ...parsingItemsRef.current[idx], status: "error" };
          }
        } catch {
          parsingItemsRef.current[idx] = { ...parsingItemsRef.current[idx], status: "error" };
        }
      }

      // 性能优化：每批处理完后，更新一次 state（大幅减少重渲染次数）
      setParsingItems([...parsingItemsRef.current]);
      
      // 让出主线程，避免阻塞 UI
      await new Promise((r) => setTimeout(r, 50));
    }

    // 处理完成后，清理预览 URL 并只保留成功结果
    if (parsedPhotos.length > 0) {
      const updated = [...customPhotos, ...parsedPhotos];
      setCustomPhotos(updated);
      localStorage.setItem("customPhotos", JSON.stringify(updated));
      toast.success(`成功添加 ${parsedPhotos.length} 张照片`);
    }

    // 清空解析列表，释放内存
    parsingItemsRef.current = [];
    setParsingItems([]);
    setUploadModalOpen(false);
  };

  const successCount = parsingItems.filter((i) => i.status === "success").length;

  return (
    <div className="min-h-screen flex flex-col" style={{ paddingTop: 64 }}>
      {/* Placeholder for fixed header */}
      <div style={{ height: headerHeight }} />

      {/* Header (fixed) */}
      <div
        ref={headerRef}
        className="flex items-center justify-between flex-shrink-0 fixed left-0 right-0 z-30"
        style={{
          top: 64,
          padding: "var(--spacing-md) var(--spacing-lg)",
          backgroundColor: "oklch(1 0 0 / 0.95)",
          backdropFilter: "blur(20px) saturate(1.4)",
          WebkitBackdropFilter: "blur(20px) saturate(1.4)",
          boxShadow: "0 1px 3px oklch(0.55 0.08 55 / 0.04)",
          borderBottom: "1px solid oklch(0.90 0.025 65 / 0.4)",
        }}
      >
        <div className="flex items-center" style={{ gap: "var(--spacing-md)" }}>
          <Link
            to="/"
            className="flex items-center rounded-xl transition-all duration-200 hover:bg-muted"
            style={{ gap: "var(--spacing-xs)", padding: "var(--spacing-xs)" }}
          >
            <ArrowLeft className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />
          </Link>
          <div>
            <h1
              style={{
                fontFamily: "var(--font-handwritten)",
                fontSize: "var(--font-size-title)",
                color: "var(--foreground)",
              }}
            >
              照片管理
            </h1>
            <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
              共 {allPhotos.length} 张照片 · 已选择 {selectedPhotos.size} 张
            </p>
          </div>
        </div>

        <div className="flex items-center" style={{ gap: "var(--spacing-sm)" }}>
          {/* Search */}
          <div
            className="flex items-center rounded-xl transition-all duration-200 focus-within:ring-2"
            style={{
              gap: "var(--spacing-xs)",
              padding: "var(--spacing-xs) var(--spacing-sm)",
              backgroundColor: "oklch(0.96 0.015 70)",
              border: "1.5px solid oklch(0.90 0.025 65 / 0.5)",
              outlineColor: "oklch(0.62 0.18 35 / 0.3)",
            }}
          >
            <Search className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />
            <input
              type="text"
              placeholder="搜索照片..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              className="bg-transparent outline-none"
              style={{ fontSize: "var(--font-size-label)", color: "var(--foreground)", width: 160 }}
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(""); setPage(1); }} className="rounded-md hover:bg-muted transition-colors p-0.5">
                <X className="w-3 h-3" style={{ color: "var(--muted-foreground)" }} />
              </button>
            )}
          </div>

          {selectedPhotos.size > 0 && (
            <>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={selectAll}
                className="rounded-xl transition-colors"
                style={{
                  padding: "var(--spacing-sm) var(--spacing-md)",
                  fontSize: "var(--font-size-label)",
                  fontWeight: 500,
                  color: "var(--muted-foreground)",
                  border: "1.5px solid oklch(0.90 0.025 65 / 0.5)",
                  backgroundColor: "oklch(1 0 0 / 0.8)",
                }}
              >
                {selectedPhotos.size === displayedPhotos.length ? "取消全选" : "全选"}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setDeleteConfirmOpen(true)}
                className="flex items-center rounded-xl transition-colors"
                style={{
                  gap: "var(--spacing-xs)",
                  padding: "var(--spacing-sm) var(--spacing-md)",
                  fontSize: "var(--font-size-label)",
                  fontWeight: 500,
                  backgroundColor: "oklch(0.65 0.18 25 / 0.08)",
                  color: "oklch(0.65 0.18 25)",
                  border: "1.5px solid oklch(0.65 0.18 25 / 0.15)",
                }}
              >
                <Trash2 className="w-4 h-4" />
                删除 ({selectedPhotos.size})
              </motion.button>
            </>
          )}

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setUploadModalOpen(true)}
            className="flex items-center rounded-xl transition-shadow duration-300"
            style={{
              gap: "var(--spacing-sm)",
              padding: "var(--spacing-sm) var(--spacing-md)",
              background: "linear-gradient(135deg, oklch(0.62 0.18 35) 0%, oklch(0.58 0.2 32) 100%)",
              color: "white",
              fontSize: "var(--font-size-label)",
              fontWeight: 500,
              boxShadow: "0 4px 12px oklch(0.55 0.15 35 / 0.25)",
            }}
          >
            <Plus className="w-4 h-4" />
            上传照片
          </motion.button>
        </div>
      </div>

      {/* Photo Grid */}
      <div
        ref={listRef}
        className="flex-1 overflow-y-auto"
        style={{ padding: "var(--spacing-lg)" }}
      >
        <div className="container mx-auto">
          {displayedPhotos.length === 0 ? (
            <div className="text-center py-20">
              <ImagePlus className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--muted-foreground)" }} />
              <p style={{ color: "var(--muted-foreground)", fontSize: "var(--font-size-body)" }}>
                {searchQuery ? "未找到匹配的照片" : "暂无照片，点击上传按钮添加"}
              </p>
            </div>
          ) : (
            <>
              <div
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
                style={{ gap: "var(--spacing-md)" }}
              >
                {displayedPhotos.map((photo) => (
                  <motion.div
                    key={photo.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="relative group cursor-pointer rounded-xl overflow-hidden"
                    style={{
                      aspectRatio: "1",
                      boxShadow: "var(--ds-shadow-sm)",
                      border: selectedPhotos.has(photo.id)
                        ? "3px solid oklch(0.62 0.18 35)"
                        : "3px solid transparent",
                    }}
                    onClick={(e) => {
                      if (e.shiftKey || e.ctrlKey || e.metaKey) {
                        toggleSelection(photo.id);
                      } else {
                        setPreviewPhoto(photo);
                      }
                    }}
                  >
                    <img
                      src={photo.thumbnail}
                      alt={photo.title}
                      className="w-full h-full object-cover transition-transform group-hover:scale-110"
                      loading="lazy"
                    />
                    {/* Overlay on hover */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end"
                      style={{
                        background: "linear-gradient(to top, oklch(0.18 0.06 45 / 0.8) 0%, transparent 60%)",
                        padding: "var(--spacing-sm)",
                      }}
                    >
                      <p
                        className="font-medium truncate"
                        style={{ fontSize: "var(--font-size-label)", color: "white" }}
                      >
                        {photo.title}
                      </p>
                      <div className="flex items-center" style={{ gap: "var(--spacing-xs)", marginTop: 2 }}>
                        <MapPin className="w-3 h-3" style={{ color: "oklch(0.70 0.14 55)" }} />
                        <span style={{ fontSize: "var(--font-size-small)", color: "oklch(0.92 0.02 65)" }}>
                          {photo.city}
                        </span>
                        <Calendar className="w-3 h-3 ml-1" style={{ color: "oklch(0.70 0.14 55)" }} />
                        <span style={{ fontSize: "var(--font-size-small)", color: "oklch(0.92 0.02 65)" }}>
                          {photo.date}
                        </span>
                      </div>
                    </div>
                    {/* Selection indicator */}
                    {selectedPhotos.has(photo.id) && (
                      <div
                        className="absolute top-2 right-2 flex items-center justify-center rounded-full"
                        style={{
                          width: 22,
                          height: 22,
                          backgroundColor: "oklch(0.62 0.18 35)",
                        }}
                      >
                        <Check className="w-3 h-3" style={{ color: "white" }} />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div
                  className="flex items-center justify-center flex-wrap"
                  style={{ gap: "var(--spacing-sm)", padding: "var(--spacing-xl) 0" }}
                >
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded-lg transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      padding: "var(--spacing-xs) var(--spacing-md)",
                      fontSize: "var(--font-size-label)",
                      fontWeight: 500,
                      backgroundColor: page <= 1 ? "oklch(0.95 0.01 70)" : "oklch(1 0 0)",
                      color: "var(--foreground)",
                      border: "1.5px solid oklch(0.90 0.025 65 / 0.5)",
                    }}
                  >
                    上一页
                  </button>

                  <div className="flex items-center" style={{ gap: "var(--spacing-xs)" }}>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className="rounded-lg transition-all duration-200"
                        style={{
                          minWidth: 36,
                          height: 36,
                          padding: "0 var(--spacing-xs)",
                          fontSize: "var(--font-size-label)",
                          fontWeight: p === page ? 600 : 400,
                          backgroundColor: p === page ? "oklch(0.62 0.18 35)" : "oklch(1 0 0)",
                          color: p === page ? "white" : "var(--foreground)",
                          border: p === page
                            ? "1.5px solid oklch(0.62 0.18 35)"
                            : "1.5px solid oklch(0.90 0.025 65 / 0.5)",
                        }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="rounded-lg transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      padding: "var(--spacing-xs) var(--spacing-md)",
                      fontSize: "var(--font-size-label)",
                      fontWeight: 500,
                      backgroundColor: page >= totalPages ? "oklch(0.95 0.01 70)" : "oklch(1 0 0)",
                      color: "var(--foreground)",
                      border: "1.5px solid oklch(0.90 0.025 65 / 0.5)",
                    }}
                  >
                    下一页
                  </button>

                  <span
                    style={{
                      fontSize: "var(--font-size-label)",
                      color: "var(--muted-foreground)",
                      marginLeft: "var(--spacing-sm)",
                    }}
                  >
                    共 {allPhotos.length} 张 · 第 {page}/{totalPages} 页
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <AnimatePresence>
        {uploadModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            style={{ backgroundColor: "oklch(0.18 0.06 45 / 0.55)" }}
            onClick={() => {
              if (parsingItems.length === 0) setUploadModalOpen(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="w-full max-w-2xl rounded-2xl overflow-hidden max-h-[85vh] flex flex-col"
              style={{
                backgroundColor: "oklch(0.99 0.01 70)",
                boxShadow: "var(--ds-shadow-xl)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                className="flex items-center justify-between flex-shrink-0"
                style={{
                  padding: "var(--spacing-md) var(--spacing-lg)",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <div className="flex items-center" style={{ gap: "var(--spacing-sm)" }}>
                  <Upload className="w-5 h-5" style={{ color: "oklch(0.62 0.18 35)" }} />
                  <h3 className="font-semibold" style={{ fontSize: "var(--font-size-title)" }}>
                    批量上传照片
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setParsingItems([]);
                    setUploadModalOpen(false);
                  }}
                  className="p-1.5 rounded-full hover:bg-muted transition-colors"
                >
                  <X className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto" style={{ padding: "var(--spacing-lg)" }}>
                {/* Upload area */}
                <div
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    processFiles(e.dataTransfer.files);
                  }}
                  className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed cursor-pointer transition-all"
                  style={{
                    padding: "var(--spacing-2xl)",
                    gap: "var(--spacing-sm)",
                    borderColor: "var(--border)",
                    backgroundColor: "oklch(0.97 0.015 70)",
                  }}
                >
                  <ImagePlus className="w-8 h-8" style={{ color: "oklch(0.62 0.18 35)" }} />
                  <p style={{ fontSize: "var(--font-size-label)", color: "var(--muted-foreground)" }}>
                    点击或拖拽照片到此处
                  </p>
                  <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                    支持批量上传，自动提取 GPS 信息
                  </p>
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => processFiles(e.target.files)}
                />

                {/* Parsing list */}
                {parsingItems.length > 0 && (
                  <div className="flex flex-col" style={{ gap: "var(--spacing-sm)", marginTop: "var(--spacing-lg)" }}>
                    <p style={{ fontSize: "var(--font-size-label)", color: "var(--muted-foreground)", fontWeight: 500 }}>
                      正在处理 {parsingItems.length} 张照片 · {successCount} 张成功
                    </p>
                    <div
                      className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6"
                      style={{ gap: "var(--spacing-sm)" }}
                    >
                      {parsingItems.slice(0, 50).map((item, idx) => (
                        <div key={idx} className="relative rounded-lg overflow-hidden" style={{ aspectRatio: "1" }}>
                          <img src={item.preview} alt={item.fileName} className="w-full h-full object-cover" />
                          {item.status === "parsing" && (
                            <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: "oklch(0.18 0.06 45 / 0.55)" }}>
                              <Loader2 className="w-6 h-6 animate-spin" style={{ color: "white" }} />
                            </div>
                          )}
                          {item.status === "success" && (
                            <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: "oklch(0.65 0.14 160 / 0.3)" }}>
                              <Check className="w-6 h-6" style={{ color: "white" }} />
                            </div>
                          )}
                          {item.status === "error" && (
                            <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: "oklch(0.65 0.18 25 / 0.3)" }}>
                              <AlertCircle className="w-6 h-6" style={{ color: "white" }} />
                            </div>
                          )}
                        </div>
                      ))}
                      {parsingItems.length > 50 && (
                        <div className="flex items-center justify-center rounded-lg" style={{ aspectRatio: "1", backgroundColor: "oklch(0.94 0.02 65)" }}>
                          <span style={{ fontSize: "var(--font-size-label)", color: "var(--muted-foreground)" }}>
                            +{parsingItems.length - 50}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirm Modal */}
      <AnimatePresence>
        {deleteConfirmOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            style={{ backgroundColor: "oklch(0.18 0.06 45 / 0.55)" }}
            onClick={() => setDeleteConfirmOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-sm rounded-2xl overflow-hidden"
              style={{
                backgroundColor: "oklch(0.99 0.01 70)",
                boxShadow: "var(--ds-shadow-xl)",
                padding: "var(--spacing-xl)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-center mb-4">
                <div
                  className="flex items-center justify-center rounded-full"
                  style={{
                    width: 48,
                    height: 48,
                    backgroundColor: "oklch(0.65 0.18 25 / 0.1)",
                  }}
                >
                  <Trash2 className="w-6 h-6" style={{ color: "oklch(0.65 0.18 25)" }} />
                </div>
              </div>
              <h3 className="text-center font-semibold mb-2" style={{ fontSize: "var(--font-size-title)" }}>
                确认删除
              </h3>
              <p className="text-center mb-6" style={{ color: "var(--muted-foreground)", fontSize: "var(--font-size-body)" }}>
                确定要删除选中的 {selectedPhotos.size} 张照片吗？此操作不可恢复。
              </p>
              <div className="flex items-center justify-center" style={{ gap: "var(--spacing-sm)" }}>
                <button
                  onClick={() => setDeleteConfirmOpen(false)}
                  className="rounded-lg transition-colors hover:bg-muted"
                  style={{
                    padding: "var(--spacing-sm) var(--spacing-lg)",
                    fontSize: "var(--font-size-label)",
                    color: "var(--muted-foreground)",
                  }}
                >
                  取消
                </button>
                <button
                  onClick={deleteSelected}
                  className="rounded-lg font-medium transition-all"
                  style={{
                    padding: "var(--spacing-sm) var(--spacing-lg)",
                    fontSize: "var(--font-size-label)",
                    backgroundColor: "oklch(0.65 0.18 25)",
                    color: "white",
                  }}
                >
                  确认删除
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Photo Preview Modal (Portal to body) */}
      {createPortal(
        <AnimatePresence>
          {previewPhoto && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[80] flex items-center justify-center p-4"
              style={{ backgroundColor: "oklch(0.18 0.06 45 / 0.85)" }}
              onClick={() => setPreviewPhoto(null)}
            >
              <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.85, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="relative max-w-[90vw] max-h-[90vh] rounded-2xl overflow-hidden"
                style={{
                  boxShadow: "0 25px 50px -12px oklch(0.18 0.06 45 / 0.5)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={previewPhoto.thumbnail}
                  alt={previewPhoto.title}
                  className="w-full h-full object-contain"
                  style={{ maxHeight: "85vh" }}
                />
                <div
                  className="absolute bottom-0 left-0 right-0"
                  style={{
                    background: "linear-gradient(to top, oklch(0.18 0.06 45 / 0.9) 0%, transparent 100%)",
                    padding: "var(--spacing-xl) var(--spacing-lg) var(--spacing-lg)",
                  }}
                >
                  <h3
                    className="font-semibold mb-2"
                    style={{ fontSize: "var(--font-size-title)", color: "white" }}
                  >
                    {previewPhoto.title}
                  </h3>
                  <div className="flex items-center flex-wrap" style={{ gap: "var(--spacing-md)" }}>
                    <div className="flex items-center" style={{ gap: "var(--spacing-xs)" }}>
                      <MapPin className="w-4 h-4" style={{ color: "oklch(0.70 0.14 55)" }} />
                      <span style={{ fontSize: "var(--font-size-label)", color: "oklch(0.92 0.02 65)" }}>
                        {previewPhoto.city}
                      </span>
                    </div>
                    <div className="flex items-center" style={{ gap: "var(--spacing-xs)" }}>
                      <Calendar className="w-4 h-4" style={{ color: "oklch(0.70 0.14 55)" }} />
                      <span style={{ fontSize: "var(--font-size-label)", color: "oklch(0.92 0.02 65)" }}>
                        {previewPhoto.date}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setPreviewPhoto(null)}
                  className="absolute top-4 right-4 p-2 rounded-full transition-colors"
                  style={{
                    backgroundColor: "oklch(0.18 0.06 45 / 0.6)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <X className="w-6 h-6" style={{ color: "white" }} />
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
